import { useEffect, useState, useCallback } from "react";
import { Percent, AlertTriangle, Plus, Trash2, Pencil, Bell } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { NudgeChannelModal } from "@/components/NudgeChannelModal";
import { toast } from "sonner";

const label = (s) => (s || "").replace(/_/g, " ");
const BLANK = { scope: "category", name: "", value: 10, comm_type: "percentage", category_id: "", seller_id: "", product_id: "", min_commission: 0 };

export function AdminCommission() {
  const [rules, setRules] = useState([]);
  const [recon, setRecon] = useState({ issues: [], count: 0 });
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [shipEarn, setShipEarn] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [accept, setAccept] = useState(null);
  const [accQ, setAccQ] = useState("");
  const [accPendingOnly, setAccPendingOnly] = useState(false);
  const [remindingAll, setRemindingAll] = useState(false);
  const [nudgeCtx, setNudgeCtx] = useState(null); // { sellerId|null, name, count }

  const load = useCallback(() => {
    api.get("/marketplace/admin/commission-rules").then((r) => setRules(r.data.rules || [])).catch(() => {});
    api.get("/marketplace/admin/reconciliation").then((r) => setRecon(r.data)).catch(() => {});
    api.get("/marketplace/admin/shipping-earnings").then((r) => setShipEarn(r.data)).catch(() => {});
    api.get("/marketplace/admin/storefronts").then((r) => setSellers((r.data.storefronts || []).filter((s) => s.seller_id).map((s) => ({ id: s.seller_id, name: s.name || s.slug || s.seller_id })))).catch(() => {});
    api.get("/admin/commission/acceptance").then((r) => setAccept(r.data)).catch(() => setAccept({ sellers: [], pending: 0, total: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remind = (s) => setNudgeCtx({ sellerId: s.seller_id, name: s.name || "seller", count: 1 });
  const remindAll = () => {
    if (!(accept && accept.pending)) return;
    setNudgeCtx({ sellerId: null, name: "", count: accept.pending });
  };
  const doNudge = async (channels) => {
    const { sellerId } = nudgeCtx;
    if (sellerId) {
      await api.post(`/admin/seller-applications/${sellerId}/commission-reminder`, { channels });
      toast.success(`Reminder sent to ${nudgeCtx.name}`);
    } else {
      setRemindingAll(true);
      try {
        const r = await api.post("/admin/commission/remind-all", { channels });
        toast.success(`Reminder sent to ${r.data.sent} pending seller(s)`);
      } finally { setRemindingAll(false); }
    }
  };

  const save = async () => {
    if (!form.name) { toast.error("Give the rule a name"); return; }
    const body = { ...form };
    ["category_id", "seller_id", "product_id"].forEach((k) => { if (!body[k]) delete body[k]; });
    try {
      if (editingId) { await api.patch(`/marketplace/admin/commission-rules/${editingId}`, body); toast.success("Commission rule updated"); }
      else { await api.post("/marketplace/admin/commission-rules", body); toast.success("Commission rule added"); }
      setForm(BLANK); setAdding(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const startEdit = (r) => { setForm({ scope: r.scope, name: r.name, value: r.value, comm_type: r.comm_type || "percentage", category_id: r.category_id || "", seller_id: r.seller_id || "", product_id: r.product_id || "", min_commission: r.min_commission || 0 }); setEditingId(r.rule_id); setAdding(true); };
  const del = async (r) => {
    if (!window.confirm(`Delete commission rule "${r.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/marketplace/admin/commission-rules/${r.rule_id}`); toast.success("Rule deleted");
      if (editingId === r.rule_id) { setEditingId(null); setForm(BLANK); setAdding(false); }
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-commission-page">
      <PageHeader title="Commission & Reconciliation" icon={Percent} subtitle="Configure marketplace commission rules. Highest-priority valid rule wins (product › seller+category › seller › category › default)." />

      {shipEarn && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="shipping-earnings">
          {[["Shipping charged to sellers", shipEarn.total_charged_to_sellers], ["Courier cost", shipEarn.courier_cost], [`Platform margin (${shipEarn.markup_pct}%)`, shipEarn.platform_margin]].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-3"><p className="text-xs text-slate-400">{k}</p><p className="mt-0.5 text-lg font-bold text-slate-800">₹{v}</p></div>
          ))}
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800">Commission Rules</h2>
        <button onClick={() => { setEditingId(null); setForm(BLANK); setAdding((v) => !v); }} data-testid="add-rule-toggle" className="inline-flex items-center gap-1 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> Add rule</button>
      </div>

      {adding && (
        <div className="mb-4 grid gap-2 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-3" data-testid="rule-form">
          <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="rule-scope" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            {["product", "seller", "seller_category", "category", "subcategory", "default"].map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rule name" data-testid="rule-name" />
          <select value={form.comm_type} onChange={(e) => setForm({ ...form, comm_type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="percentage">Percentage</option><option value="fixed">Fixed ₹</option></select>
          <Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} placeholder="Value" data-testid="rule-value" />
          <Input value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} placeholder="Category ID (optional)" />
          {["seller", "seller_category"].includes(form.scope) ? (
            <select value={form.seller_id} onChange={(e) => setForm({ ...form, seller_id: e.target.value })} data-testid="rule-seller" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="">Select seller…</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          ) : (
            <Input value={form.seller_id} onChange={(e) => setForm({ ...form, seller_id: e.target.value })} placeholder="Seller ID (optional)" data-testid="rule-seller-id" />
          )}
          <button onClick={save} data-testid="rule-save" className="rounded-lg brand-gradient-bg px-4 py-2 text-sm font-semibold text-white sm:col-span-3">{editingId ? "Update rule" : "Save rule"}</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="rules-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Name</th><th className="p-3">Scope</th><th className="p-3">Rate</th><th className="p-3">Priority</th><th className="p-3">Active</th><th className="p-3"></th></tr></thead>
          <tbody>{(() => { const defaultCount = rules.filter((x) => x.scope === "default").length; return rules.map((r) => (
            <tr key={r.rule_id} className="border-t border-slate-50" data-testid={`rule-${r.rule_id}`}>
              <td className="p-3 font-medium text-slate-800">{r.name}</td><td className="p-3 capitalize">{label(r.scope)}{r.seller_id && <span className="ml-1 text-xs text-slate-400">· {(sellers.find((s) => s.id === r.seller_id) || {}).name || r.seller_id}</span>}</td>
              <td className="p-3">{r.comm_type === "fixed" ? `₹${r.value}` : `${r.value}%`}</td><td className="p-3">{r.priority}</td>
              <td className="p-3">{r.active ? "Yes" : "No"}</td>
              <td className="p-3"><div className="flex items-center gap-3">
                <button onClick={() => startEdit(r)} data-testid={`edit-rule-${r.rule_id}`} className="text-slate-500 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                {(r.scope !== "default" || defaultCount > 1) && <button onClick={() => del(r)} data-testid={`del-rule-${r.rule_id}`} title="Delete rule" className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>}
              </div></td>
            </tr>)); })()}</tbody>
        </table>
      </div>

      <div className="mt-6" data-testid="commission-acceptance">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="font-semibold text-slate-800">Seller acceptance</h2>
          {accept && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="acceptance-pending">{accept.pending} pending · {accept.total} sellers</span>}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input value={accQ} onChange={(e) => setAccQ(e.target.value)} data-testid="acceptance-search" placeholder="Search seller…" className="h-8 w-48 rounded-full border border-slate-200 px-3 text-xs outline-none focus:border-amber-400" />
            <button onClick={() => setAccPendingOnly((v) => !v)} data-testid="acceptance-pending-toggle" className={`rounded-full px-3 py-1.5 text-xs font-semibold ${accPendingOnly ? "bg-amber-500 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>Pending only</button>
            <button onClick={remindAll} disabled={remindingAll || !(accept && accept.pending)} data-testid="acceptance-remind-all" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><Bell className="h-3.5 w-3.5" /> {remindingAll ? "Sending…" : "Remind all pending"}</button>
          </div>
        </div>
        {accept && accept.total > 0 && (() => {
          const acceptedN = Math.max(0, (accept.total || 0) - (accept.pending || 0));
          const pct = Math.round((acceptedN / accept.total) * 100);
          return (
            <div className="mb-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="acceptance-progress">
              <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-600">
                <span data-testid="acceptance-progress-label">{acceptedN} of {accept.total} sellers accepted</span>
                <span data-testid="acceptance-progress-pct" className={pct === 100 ? "text-emerald-600" : "text-slate-500"}>{pct}%</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div data-testid="acceptance-progress-bar" className={`h-full rounded-full transition-all ${pct === 100 ? "bg-emerald-500" : "brand-gradient-bg"}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })()}
        {!accept ? (
          <p className="rounded-2xl border border-slate-100 p-6 text-center text-sm text-slate-400">Loading…</p>
        ) : (() => {
          const q = accQ.trim().toLowerCase();
          const list = accept.sellers.filter((s) => (!accPendingOnly || s.state === "not_accepted") && (!q || (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q)));
          return list.length === 0 ? (
            <p className="rounded-2xl border border-slate-100 p-6 text-center text-sm text-slate-400" data-testid="acceptance-empty">No sellers match.</p>
          ) : (
            <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
              {list.map((s) => (
                <div key={s.seller_id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`acceptance-row-${s.seller_id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800">{s.name || "Seller"}</p>
                    <p className="text-xs text-slate-400">{s.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.state === "accepted" ? (
                      <span className="text-right" data-testid={`acceptance-state-${s.seller_id}`}>
                        <span className="block rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">Accepted</span>
                        <span className="mt-0.5 block text-[11px] text-slate-400" data-testid={`acceptance-date-${s.seller_id}`}>{s.accepted_at ? `on ${String(s.accepted_at).slice(0, 10)}` : "date n/a"}</span>
                      </span>
                    ) : (
                      <>
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700" data-testid={`acceptance-state-${s.seller_id}`}>Pending</span>
                        <button onClick={() => remind(s)} data-testid={`acceptance-remind-${s.seller_id}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                          <Bell className="h-3.5 w-3.5" /> Remind
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/40 p-4" data-testid="reconciliation">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Reconciliation Required ({recon.count})</div>
        {recon.count === 0 ? <p className="text-sm text-slate-500">No financial mismatches detected.</p> : recon.issues.map((i, idx) => (
          <p key={idx} className="border-t border-amber-100 py-1.5 text-sm text-slate-700 first:border-t-0">{label(i.type)} — {i.number || i.suborder_id} {i.detail || (i.expected != null ? `(stored ₹${i.stored} vs expected ₹${i.expected})` : "")}</p>
        ))}
      </div>

      {nudgeCtx && (
        <NudgeChannelModal
          open={!!nudgeCtx}
          action="commission-reminder"
          sellerId={nudgeCtx.sellerId}
          sellerName={nudgeCtx.name}
          count={nudgeCtx.count}
          onSend={async (channels) => { await doNudge(channels); load(); }}
          onClose={() => setNudgeCtx(null)}
          testid="commission-nudge-modal"
        />
      )}
    </div>
  );
}
